<script setup>
import { computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import LaisGamificationSurface from 'dashboard/components-next/LaisGamificationSurface.vue';
import SalesRegistrationDeliveryNotice from '../components/SalesRegistrationDeliveryNotice.vue';
import SalesRegistrationCards from '../components/SalesRegistrationCards.vue';
import SalesRegistrationFooter from '../components/SalesRegistrationFooter.vue';
import SalesRegistrationHeader from '../components/SalesRegistrationHeader.vue';
import SalesRegistrationIntroDialog from '../components/SalesRegistrationIntroDialog.vue';
import SalesRegistrationMobileFooter from '../components/SalesRegistrationMobileFooter.vue';
import { useSalesRegistration } from '../composables/useSalesRegistration';
import { salesIndexLocation } from '../composables/useSalesRouteState';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const currentUser = useMapGetter('getCurrentUser');
const id = computed(() => route.params.salesTransactionId);
const registration = useSalesRegistration({
  id,
  t,
  notify: useAlert,
});

onMounted(() => window.sessionStorage.removeItem('urlCadastro'));

const goBack = () =>
  router.push(
    salesIndexLocation({
      accountId: route.params.accountId,
      query: route.query,
      fallbackList: 'rascunhos',
    })
  );
const goHistoryBack = () => router.back();

watch(
  () => registration.isForbidden.value,
  forbidden => {
    if (forbidden) goBack();
  }
);

const selectBlock = block => {
  const routeNames = {
    imovel: 'operacao_imobiliaria_vendas_cadastro_imovel',
    vendedores: 'operacao_imobiliaria_vendas_cadastro_partes',
    compradores: 'operacao_imobiliaria_vendas_cadastro_partes',
    recibo: 'operacao_imobiliaria_vendas_cadastro_recibo',
    comissao: 'operacao_imobiliaria_vendas_comissao',
  };
  const name = routeNames[block];
  if (!name) return;

  router.push({
    name,
    params: {
      accountId: route.params.accountId,
      salesTransactionId: id.value,
      ...(block === 'vendedores' || block === 'compradores'
        ? { partyRole: block === 'vendedores' ? 'vendedor' : 'comprador' }
        : {}),
    },
    query: route.query,
  });
};
const goDelivery = () =>
  router.push({
    name: 'operacao_imobiliaria_vendas_entregar',
    params: {
      accountId: route.params.accountId,
      salesTransactionId: id.value,
    },
    query: route.query,
  });
</script>

<template>
  <LaisGamificationSurface
    :user-id="currentUser?.id"
    show-rail
    class="min-h-full bg-lais-bg-soft"
  >
    <SalesRegistrationIntroDialog />
    <SalesRegistrationHeader
      v-if="registration.transaction.value"
      :address="registration.transaction.value.address"
      @back="goBack"
      @exit="goBack"
    />

    <main
      class="mx-auto flex w-full max-w-[1500px] flex-col gap-5 px-4 pb-32 sm:px-6 lg:px-10"
    >
      <div
        v-if="registration.loading.value"
        class="rounded-lais border border-lais-border-soft bg-white p-8 text-center text-sm text-lais-text-muted"
      >
        {{ t('SALES.COMMON.LOADING') }}
      </div>
      <SalesRegistrationCards
        v-else-if="registration.transaction.value"
        :progress="registration.transaction.value.block_progress"
        @select="selectBlock"
      />
      <SalesRegistrationDeliveryNotice
        v-if="registration.transaction.value"
        :progress="registration.transaction.value.block_progress"
      />
    </main>
    <SalesRegistrationFooter
      v-if="registration.transaction.value"
      :progress="registration.transaction.value.progress?.percent || 0"
      @deliver="goDelivery"
    />
    <SalesRegistrationMobileFooter @back="goHistoryBack" />
  </LaisGamificationSurface>
</template>
